import React, { useEffect, useState } from "react";

export default function AccountControls() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadSettings = async () => {
    setLoading(true);
    setLoaded(false);
    setError("");
    try {
      const response = await fetch("/api/account-controls", { credentials: "include", cache: "no-store" });
      if (!response.ok) throw new Error("Unable to load account controls. Please retry.");
      const data = await response.json();
      setEnabled(data.accountDeletionEnabled === true);
      setLoaded(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const toggle = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch("/api/admin/account-controls", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ accountDeletionEnabled: !enabled }),
      });
      if (!response.ok) throw new Error("Unable to save account controls. Please retry.");
      const data = await response.json();
      setEnabled(data.accountDeletionEnabled === true);
      setMessage(data.accountDeletionEnabled ? "Delete account is now visible to users." : "Delete account is now hidden from users.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Account controls</h2>
          <p id="account-deletion-help" className="mt-1 text-sm text-slate-500">
            Show the Delete account option on users' account pages. Hidden by default.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-600">{loading ? "Loading..." : saving ? "Saving..." : enabled ? "Visible" : "Hidden"}</span>
          <button
            type="button"
            role="switch"
            aria-label="Show Delete account option"
            aria-describedby="account-deletion-help"
            aria-checked={enabled}
            disabled={!loaded || loading || saving}
            onClick={toggle}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 ${enabled ? "bg-purple-600" : "bg-slate-300"}`}
          >
            <span className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error} <button type="button" onClick={loadSettings} disabled={loading || saving} className="font-semibold underline">Reload settings</button></p>}
      {message && <p role="status" className="mt-3 text-sm text-green-700">{message}</p>}
    </section>
  );
}
